import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {UnifiedCodeRow, DELIVERY_LABEL_KEY} from './unifyCodeRows.ts'
import {CodeStatusPill, LIFECYCLE_EDITABLE} from './CodeStatusPill.tsx'
import {CodeStatusMenu} from './CodeStatusMenu.tsx'
import {TripCell, Dash, downloadEbookPdf, regenerateEbookPdf} from './PackActivityTables.tsx'
import {formatDate} from '../../utils/formatDate.ts'
import IconClose from '../../assets/icons/close.svg?react'
import IconEdit from '../../assets/icons/edit.svg?react'
import IconPlus from '../../assets/icons/plus.svg?react'
import IconBook from '../../assets/icons/book.svg?react'
import IconPdfDoc from '../../assets/icons/pdf-doc.svg?react'
import IconDownload from '../../assets/icons/download-arrow.svg?react'
import IconSpinner from '../../assets/icons/spinner.svg?react'

const STAGE_LABEL_KEYS = ['packs.statusDelivered', 'packs.statusTripCreated', 'packs.statusPrinted'] as const

// Stage reached, 0-indexed into STAGE_LABELS — -1 means not even delivered yet.
function stageIndex(lifecycle: UnifiedCodeRow['lifecycle']): number {
    switch (lifecycle) {
        case 'DELIVERED': return 0
        case 'TRIP_CREATED': return 1
        case 'PRINTED': return 2
        default: return -1
    }
}

interface TripbookSidePanelProps {
    row: UnifiedCodeRow
    packContext: string
    canEditCode: boolean
    // Feedback is written only by 51trips staff — the agency sees it read-only.
    canEditFeedback: boolean
    isDelivered: boolean
    // The traveler-facing itinerary attachment (InvitationCodeItinerary.tripItineraryPdfUrl)
    // — uploaded or pasted via ItineraryAttachmentField. Looked up by the caller from
    // invitationCodesById, since it lives on the live code record, not on UnifiedCodeRow.trip.
    itineraryUrl?: string | null
    onClose: () => void
    onEditTraveler: () => void
    onEditTrip: () => void
    onEditFeedback: () => void
    onStatusChange?: (delivered: boolean) => void
}

// Mirrors the design's `panel` slide-over — full detail of one code/tripbook.
// "Páginas extra" and "Productor" from the mock are omitted: there's no data
// source for either yet (not part of this pass — see plan notes).
export const TripbookSidePanel: React.FC<TripbookSidePanelProps> = ({
    row, packContext, canEditCode, canEditFeedback, isDelivered, itineraryUrl, onClose, onEditTraveler, onEditTrip, onEditFeedback, onStatusChange,
}) => {
    const {t} = useTranslation('dashboard')
    const [menuOpen, setMenuOpen] = useState(false)
    // One request at a time — a regenerate re-renders the PDF (up to 90s), so a
    // download fired meanwhile would just fetch the stale one.
    const [pdfAction, setPdfAction] = useState<'download' | 'regenerate' | null>(null)
    const runPdfAction = (action: 'download' | 'regenerate', code: string) => {
        setPdfAction(action)
        const request = action === 'download' ? downloadEbookPdf(code) : regenerateEbookPdf(code)
        request.finally(() => setPdfAction(null))
    }
    const stage = stageIndex(row.lifecycle)
    const editableStatus = LIFECYCLE_EDITABLE.includes(row.lifecycle) && canEditCode && !!onStatusChange
    const travelerName = row.traveler?.name
    const initials = travelerName ? travelerName.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() : '—'

    return (
        <div className="fixed inset-0 z-40">
            <div onClick={onClose} className="absolute inset-0 bg-neutralBlack-900/25"/>
            <aside className="absolute top-0 right-0 bottom-0 w-[520px] max-w-[92vw] bg-white border-l border-neutralBlack-200 shadow-2xl flex flex-col">
                <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-[18px] border-b border-neutralBlack-200">
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                        <span className="flex items-center gap-2.5 flex-wrap relative">
                            <span className="text-[19px] font-semibold tracking-tight text-neutralBlack-900">{row.code ?? '—'}</span>
                            <CodeStatusPill lifecycle={row.lifecycle} onClick={editableStatus ? () => setMenuOpen((v) => !v) : undefined}/>
                            {menuOpen && editableStatus && (
                                <CodeStatusMenu
                                    delivered={isDelivered}
                                    onChange={(delivered) => onStatusChange?.(delivered)}
                                    onClose={() => setMenuOpen(false)}
                                />
                            )}
                        </span>
                        <span className="text-[13.5px] text-neutralBlack-500">{(travelerName ?? t('packs.statusNotAssigned')) + (packContext ? ' · ' + packContext : '')}</span>
                    </div>
                    <button type="button" onClick={onClose}
                            className="w-8 h-8 flex-none flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200">
                        <IconClose className="size-3"/>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-[22px] pb-10 flex flex-col gap-6">
                    <section className="flex flex-col gap-3.5">
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelLifecycle')}</span>
                        {STAGE_LABEL_KEYS.map((labelKey, i) => {
                            const hecho = i <= stage
                            return (
                                <div key={labelKey} className="flex gap-3.5 items-start">
                                    <span className="flex flex-col items-center flex-none w-[22px]">
                                        <span className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-white border ${hecho ? 'bg-neutralBlack-900 border-neutralBlack-900' : 'bg-white border-neutralBlack-300'}`}>
                                            {hecho && <span className="w-2.5 h-2.5 rounded-full bg-white"/>}
                                        </span>
                                        {i < STAGE_LABEL_KEYS.length - 1 && <span className="w-px flex-1 min-h-5 bg-neutralBlack-200"/>}
                                    </span>
                                    <span className="flex flex-col gap-0.5 pb-3.5">
                                        <span className={`text-sm font-medium ${hecho ? 'text-neutralBlack-900' : 'text-neutralBlack-400'}`}>{t(labelKey)}</span>
                                        <span className="text-[12.5px] text-neutralBlack-500">
                                            {i === 2 && hecho ? formatDate(row.delivery?.orderedAt ?? null) : hecho ? '—' : t('packs.sidePanelPending')}
                                        </span>
                                    </span>
                                </div>
                            )
                        })}
                    </section>

                    <section className="flex flex-col gap-3">
                        <span className="flex items-center gap-2.5">
                            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelTraveler')}</span>
                            <span className="flex-1"/>
                            {canEditCode && (
                                <button type="button" onClick={onEditTraveler}
                                        className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-50">
                                    <IconEdit className="size-3 text-neutralBlack-400"/>
                                    {t('packs.sidePanelEditTraveler')}
                                </button>
                            )}
                        </span>
                        <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex flex-col gap-2.5 text-sm">
                            <Row label={t('packs.sidePanelName')} value={row.traveler?.name ?? t('packs.sidePanelNameUnassigned')} strong/>
                            <Row label={t('phone')} value={row.traveler?.phone ?? '—'}/>
                            <Row label={t('email')} value={row.traveler?.email ?? '—'}/>
                        </div>
                    </section>

                    <section className="flex flex-col gap-3">
                        <span className="flex items-center gap-2.5">
                            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelTrip')}</span>
                            <span className="flex-1"/>
                            {canEditCode && (
                                <button type="button" onClick={onEditTrip}
                                        className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-50">
                                    <IconEdit className="size-3 text-neutralBlack-400"/>
                                    {t('packs.sidePanelEditTrip')}
                                </button>
                            )}
                        </span>
                        <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex flex-col gap-2.5 text-sm">
                            {row.trip ? (
                                <span className="flex items-center justify-between gap-3">
                                    <TripCell trip={row.trip}/>
                                    {itineraryUrl && (
                                        <a href={itineraryUrl} target="_blank" rel="noreferrer"
                                           className="flex-none inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-100 whitespace-nowrap">
                                            {t('packs.viewItinerary')}
                                        </a>
                                    )}
                                </span>
                            ) : <Dash/>}
                            {row.deliverableUrl && (
                                <>
                                    <span className="h-px bg-neutralBlack-200 my-0.5"/>
                                    <span className="text-[12.5px] leading-relaxed text-neutralBlack-500">
                                        {t('packs.sidePanelTripHelp')}
                                    </span>
                                    <a href={row.deliverableUrl} target="_blank" rel="noreferrer" className="text-[13.5px]">{row.deliverableUrl}</a>
                                </>
                            )}
                        </div>
                    </section>

                    <section className="flex flex-col gap-3">
                        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelDigitalTripbook')}</span>
                        <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex items-center gap-3.5 flex-wrap">
                            <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-white border border-neutralBlack-200 flex items-center justify-center text-neutralBlack-700">
                                <IconBook className="size-4"/>
                            </span>
                            <span className="flex-1 min-w-[120px] flex flex-col gap-0.5">
                                <span className="text-[13.5px] font-medium text-neutralBlack-900">
                                    {row.flipbookUrl ? t('packs.sidePanelPublished') : t('packs.sidePanelNotPublished')}
                                </span>
                                <span className="text-xs text-neutralBlack-500">
                                    {row.flipbookUrl ? t('packs.sidePanelPublishedBy51trips') : t('packs.sidePanelWillAddWhenReady')}
                                </span>
                            </span>
                            {row.flipbookUrl && (
                                <a href={row.flipbookUrl} target="_blank" rel="noreferrer"
                                   className="h-8 inline-flex items-center px-3.5 rounded-full border border-neutralBlack-200 bg-white text-xs text-neutralBlack-700 hover:bg-neutralBlack-100 whitespace-nowrap text-[#3E63DD] hover:underline">
                                    {t('packs.viewTripbook')}
                                </a>
                            )}
                        </div>
                    </section>

                    {row.code && (
                        <section className="flex flex-col gap-3">
                            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelPdf')}</span>
                            <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex items-center gap-3.5 flex-wrap">
                                <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-[#F7E8E9] border border-[#8C2F39]/[.14] flex items-center justify-center">
                                    <IconPdfDoc className="w-[13px] h-[15px] text-[#8C2F39]"/>
                                </span>
                                <span className="flex-1 min-w-[120px] flex flex-col gap-0.5">
                                    <span className="text-[13.5px] font-medium text-neutralBlack-900">{`${row.code}.pdf`}</span>
                                    <span className="text-xs text-neutralBlack-500">{t('packs.sidePanelPdfHelp')}</span>
                                </span>
                                <button type="button" disabled={pdfAction !== null} onClick={() => runPdfAction('regenerate', row.code!)}
                                        className="h-8 inline-flex items-center gap-1.5 px-3.5 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-100 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                                    {pdfAction === 'regenerate' && <IconSpinner className="size-3.5"/>}
                                    {pdfAction === 'regenerate' ? t('packs.regeneratingPdf') : t('packs.regeneratePdf')}
                                </button>
                                <button type="button" disabled={pdfAction !== null} onClick={() => runPdfAction('download', row.code!)}
                                        className="h-8 inline-flex items-center gap-1.5 px-3.5 rounded-full bg-neutralBlack-900 text-white text-[12.5px] font-medium hover:bg-neutralBlack-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                                    {pdfAction === 'download' ? <IconSpinner className="size-3.5"/> : <IconDownload className="size-3"/>}
                                    {t('packs.downloadPdf')}
                                </button>
                            </div>
                        </section>
                    )}

                    <section className="flex flex-col gap-3">
                        <span className="flex items-center gap-2.5">
                            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelFeedback')}</span>
                            <span className="flex-1"/>
                            {canEditFeedback && (
                                <button type="button" disabled={!row.tripbookId} onClick={onEditFeedback}
                                        className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-full border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-700 hover:bg-neutralBlack-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                                    <IconEdit className="size-3 text-neutralBlack-400"/>
                                    {t('packs.edit')}
                                </button>
                            )}
                        </span>
                        {row.feedback ? (
                            <div className="flex gap-2.5 items-end">
                                <span className="w-[30px] h-[30px] flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-[11px] font-semibold text-neutralBlack-700">{initials}</span>
                                <span className="flex-1 min-w-0 bg-success/10 border border-success/30 rounded-2xl rounded-bl-md px-3.5 py-3 flex flex-col gap-1.5">
                                    <span className="text-xs font-semibold text-success">{travelerName ?? t('packs.sidePanelTraveler')}</span>
                                    <span className="text-[13.5px] leading-relaxed text-neutralBlack-900">{row.feedback.comment}</span>
                                    <span className="self-end text-[11px] text-neutralBlack-500">
                                        {row.feedback.channel} · {formatDate(row.feedback.feedbackDate)}
                                    </span>
                                </span>
                            </div>
                        ) : (
                            <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex items-center gap-3">
                                <span className="w-[30px] h-[30px] flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-neutralBlack-400">
                                    <IconPlus className="size-3"/>
                                </span>
                                <span className="flex-1 min-w-[120px] text-[13.5px] text-neutralBlack-500">
                                    {row.tripbookId ? t('packs.sidePanelNoFeedbackYet') : t('packs.sidePanelNoTripYetForFeedback')}
                                </span>
                                {row.tripbookId && canEditFeedback && (
                                    <button type="button" onClick={onEditFeedback}
                                            className="h-8 inline-flex items-center gap-1.5 px-3.5 rounded-full bg-neutralBlack-900 text-white text-[12.5px] font-medium hover:bg-neutralBlack-700 whitespace-nowrap">
                                        <IconPlus className="size-2.5"/>
                                        {t('packs.sidePanelAddFeedback')}
                                    </button>
                                )}
                            </div>
                        )}
                    </section>

                    {row.delivery && (
                        <section className="flex flex-col gap-3">
                            <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('packs.sidePanelShippingTitle')}</span>
                            <div className="bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-4 flex flex-col gap-2.5 text-sm">
                                <Row label={t('packs.sidePanelStatus')} value={row.delivery.status in DELIVERY_LABEL_KEY ? t(DELIVERY_LABEL_KEY[row.delivery.status]) : row.delivery.status} strong/>
                                <Row label={t('packs.sidePanelOrder')} value={formatDate(row.delivery.orderedAt)}/>
                                {row.delivery.trackingUrl ? (
                                    <span className="flex justify-between gap-4">
                                        <span className="text-blue-500">{t('packs.sidePanelTracking')}</span>
                                        <a href={row.delivery.trackingUrl} className="text-blue-500" target="_blank" rel="noreferrer">{t('packs.sidePanelFollowOrder')}</a>
                                    </span>
                                ) : <Row label={t('packs.sidePanelTracking')} value="—"/>}
                            </div>
                        </section>
                    )}
                </div>
            </aside>
        </div>
    )
}

const Row: React.FC<{label: string; value: string; strong?: boolean}> = ({label, value, strong}) => (
    <span className="flex justify-between gap-4">
        <span className="text-neutralBlack-500">{label}</span>
        <span className={`text-right ${strong ? 'font-medium text-neutralBlack-900' : 'text-neutralBlack-700'}`}>{value}</span>
    </span>
)
